import React from 'react';
import { StepBar } from '../ui';

interface DiscoveryStepBarProps {
  readOnly: boolean;
  totalComponents: number;
  /** Components with at least one supplier found so far. */
  withSuppliers: number;
  totalPicked: number;
  componentsPicked: number;
  saving: boolean;
  onBack: () => void;
  onContinue?: () => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Discovery's fixed bottom bar: the whole-BOM shortlist in one line, and the way on. */
export const DiscoveryStepBar: React.FC<DiscoveryStepBarProps> = ({
  readOnly,
  totalComponents,
  withSuppliers,
  totalPicked,
  componentsPicked,
  saving,
  onBack,
  onContinue,
}) => {
  const stepLabel = 'Step 3 · Discovery · Whole BOM';
  if (readOnly || !onContinue) {
    return (
      <StepBar
        stepLabel={stepLabel}
        summary={`${withSuppliers} of ${totalComponents} components have suppliers`}
        onBack={onBack}
        cta={{ label: 'Continue to outreach', onClick: onContinue, disabled: !onContinue }}
      />
    );
  }
  if (totalPicked === 0) {
    return (
      <StepBar
        stepLabel={stepLabel}
        summary={
          <>
            <b className="text-ink">0 suppliers</b> shortlisted · {withSuppliers} of {totalComponents} components have
            suppliers so far · <span className="font-semibold text-warn-ink">shortlist one to continue</span>
          </>
        }
        onBack={onBack}
        cta={{ label: 'Continue to outreach', disabled: true }}
      />
    );
  }
  const notPicked = totalComponents - componentsPicked;
  return (
    <StepBar
      stepLabel={stepLabel}
      summary={
        <>
          <b className="text-ink">{plural(totalPicked, 'supplier')}</b> shortlisted across {componentsPicked} of{' '}
          {totalComponents} components
          {notPicked > 0 && (
            <>
              {' · '}
              <span className="font-semibold text-warn-ink">{notPicked} not picked yet</span>
            </>
          )}
        </>
      }
      onBack={onBack}
      cta={{
        label: `Continue to outreach with ${plural(totalPicked, 'supplier')}`,
        onClick: onContinue,
        disabled: saving,
      }}
    />
  );
};

export default DiscoveryStepBar;
